import { twMerge } from 'tailwind-merge';
import { LagBadge } from './EpcScheduleLinkDesign';
import {
  findTaskIndexByMspTaskId,
  findTaskIndexByUniqueId,
  linkPillClass,
  predecessorDisplayLabel,
  resolvePredecessorTaskIndex,
  resolveSuccessorTaskIndex,
  successorDisplayLabel,
  type EpcTaskRef,
  type ParsedPredecessorLink,
  type ParsedSuccessorLink,
} from './utils/epcScheduleDependencies';
import { formatLagSegment, parseMsDependencySegment } from './utils/epcScheduleColumnFormat';

type Variant = 'predecessor' | 'successor';

const VARIANT_RING: Record<Variant, string> = {
  predecessor: 'ring-slate-200/80 bg-slate-50/90',
  successor: 'ring-sky-200/80 bg-sky-50/90',
};

const VARIANT_NAME: Record<Variant, string> = {
  predecessor: 'text-teal-800 hover:text-teal-950',
  successor: 'text-sky-900 hover:text-sky-950',
};

function DependencyRow({
  variant,
  linkType,
  lagDisplay,
  title,
  driverName,
  taskNumber,
  targetIndex,
  fromRowIndex,
  onJump,
}: {
  variant: Variant;
  linkType: string;
  lagDisplay: string | undefined;
  title: string;
  driverName: string;
  taskNumber?: string | number;
  targetIndex: number | null;
  fromRowIndex: number;
  onJump: (toRowIndex: number, fromRowIndex: number) => void;
}) {
  /** Never treat same-row as a jump target (defense in depth vs resolver / stale bundles). */
  const effectiveTarget =
    targetIndex != null && targetIndex === fromRowIndex ? null : targetIndex;
  const canGo = effectiveTarget != null;

  return (
    <div
      className={twMerge(
        'flex min-w-0 flex-col gap-1 rounded-md px-2 py-1.5 ring-1',
        VARIANT_RING[variant]
      )}
    >
      <button
        type="button"
        title={title}
        disabled={!canGo}
        onClick={(e) => {
          e.stopPropagation();
          if (effectiveTarget != null) onJump(effectiveTarget, fromRowIndex);
        }}
        className={twMerge(
          'flex min-w-0 items-baseline gap-1 text-left text-[11px] font-semibold leading-snug transition',
          canGo ? `${VARIANT_NAME[variant]} underline-offset-2 hover:underline` : 'cursor-default text-slate-500 no-underline'
        )}
      >
        {taskNumber != null && taskNumber !== '' ? (
          <span className="shrink-0 rounded bg-slate-200/80 px-1 py-0.5 font-mono text-[9px] font-bold text-slate-700">
            #{taskNumber}
          </span>
        ) : null}
        <span className="min-w-0 truncate">{driverName}</span>
      </button>
      <div className="flex flex-wrap items-center gap-1">
        <span
          className={twMerge(
            'shrink-0 rounded px-1 py-0.5 font-mono text-[8px] font-black',
            linkPillClass(linkType)
          )}
        >
          {linkType}
        </span>
        {lagDisplay ? <LagBadge lag={lagDisplay} /> : null}
      </div>
    </div>
  );
}

export function EpcSchedulePredecessorsColumn({
  links,
  tasks,
  fromRowIndex,
  onJumpToLinkedTask,
}: {
  links: ParsedPredecessorLink[];
  tasks: EpcTaskRef[];
  fromRowIndex: number;
  onJumpToLinkedTask: (toRowIndex: number, fromRowIndex: number) => void;
}) {
  if (!links.length) {
    return <span className="text-[11px] text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-col gap-2">
      {links.map((lk, i) => {
        const idx = resolvePredecessorTaskIndex(lk, tasks, fromRowIndex);
        const driverName = predecessorDisplayLabel(lk, tasks, fromRowIndex);
        const lagPretty = lk.lag ? formatLagSegment(lk.lag) : '';
        const lagShow = lagPretty || (lk.lag ? lk.lag : undefined);
        const taskNumber =
          idx != null
            ? idx + 1
            : lk.predecessorTaskId != null && lk.predecessorTaskId !== ''
              ? String(lk.predecessorTaskId)
              : undefined;
        return (
          <DependencyRow
            key={i}
            variant="predecessor"
            linkType={lk.linkTypeCode}
            lagDisplay={lagShow}
            title={
              idx != null
                ? `#${idx + 1} · ${lk.linkTypeCode}${lagShow ? ` · ${lagShow}` : ''} — jump to row`
                : 'Could not link — re-import .mpp after converter update, or check task IDs'
            }
            driverName={driverName}
            taskNumber={taskNumber}
            targetIndex={idx}
            fromRowIndex={fromRowIndex}
            onJump={onJumpToLinkedTask}
          />
        );
      })}
    </div>
  );
}

export function EpcScheduleSuccessorsColumn({
  links,
  tasks,
  fromRowIndex,
  onJumpToLinkedTask,
}: {
  links: ParsedSuccessorLink[];
  tasks: EpcTaskRef[];
  fromRowIndex: number;
  onJumpToLinkedTask: (toRowIndex: number, fromRowIndex: number) => void;
}) {
  if (!links.length) {
    return <span className="text-[11px] text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-col gap-2">
      {links.map((lk, i) => {
        const idx = resolveSuccessorTaskIndex(lk, tasks, fromRowIndex);
        const driverName = successorDisplayLabel(lk, tasks, fromRowIndex);
        const lagPretty = lk.lag ? formatLagSegment(lk.lag) : '';
        const lagShow = lagPretty || (lk.lag ? lk.lag : undefined);
        return (
          <DependencyRow
            key={i}
            variant="successor"
            linkType={lk.linkTypeCode}
            lagDisplay={lagShow}
            title={
              idx != null
                ? `${lk.linkTypeCode}${lagShow ? ` · ${lagShow}` : ''} — jump to row`
                : 'Could not link — re-import .mpp after converter update, or check task IDs'
            }
            driverName={driverName}
            targetIndex={idx}
            fromRowIndex={fromRowIndex}
            onJump={onJumpToLinkedTask}
          />
        );
      })}
    </div>
  );
}

export function EpcScheduleRawMsDependencyColumn({
  segments,
  tasks,
  variant,
  fromRowIndex,
  onJumpToLinkedTask,
}: {
  segments: string[];
  tasks: EpcTaskRef[];
  variant: Variant;
  fromRowIndex: number;
  onJumpToLinkedTask: (toRowIndex: number, fromRowIndex: number) => void;
}) {
  if (!segments.length) {
    return <span className="text-[11px] text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-col gap-2">
      {segments.map((seg, i) => {
        const parsed = parseMsDependencySegment(seg);
        if (!parsed) {
          return (
            <div
              key={i}
              className={twMerge(
                'rounded-md px-2 py-1 font-mono text-[10px] leading-snug text-slate-900 ring-1',
                VARIANT_RING[variant]
              )}
            >
              {seg}
            </div>
          );
        }
        const uidNum = parsed.idStr === '?' ? NaN : Number(parsed.idStr);
        let idx = Number.isFinite(uidNum)
          ? findTaskIndexByMspTaskId(tasks, uidNum) ?? findTaskIndexByUniqueId(tasks, uidNum)
          : null;
        if (idx === fromRowIndex) {
          idx = null;
        }
        const match = idx != null ? tasks[idx] : undefined;
        const nm = match && typeof match.name === 'string' ? match.name.trim() : '';
        const driverName = nm || (parsed.idStr === '?' ? 'Unknown task' : `Task id ${parsed.idStr}`);
        const lagShow = parsed.lagTail ? formatLagSegment(parsed.lagTail) || parsed.lagTail : undefined;
        const taskNumber =
          idx != null ? idx + 1 : parsed.idStr !== '?' ? parsed.idStr : undefined;
        return (
          <DependencyRow
            key={i}
            variant={variant}
            linkType={parsed.linkType}
            lagDisplay={lagShow}
            title={
              idx != null
                ? `#${idx + 1} · ${parsed.linkType}${lagShow ? ` · ${lagShow}` : ''} — jump to row`
                : `${seg} — no matching row`
            }
            driverName={driverName}
            taskNumber={taskNumber}
            targetIndex={idx}
            fromRowIndex={fromRowIndex}
            onJump={onJumpToLinkedTask}
          />
        );
      })}
    </div>
  );
}
